import * as Actions from '../actions/lookupActions';
import Lookup from '../../entities/lookup';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type LookupAction = { 
    type: string; 
    lookup: Lookup | null; 
    lookupId: string | Lookup; 
    partial: boolean; 
    lastFetch: number; 
    lookups: Lookup[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Lookup> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Lookup[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Lookup> | null, firmId: string, userId: string, updates?: Lookup[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'lookup', 'lookups', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Lookup>} data
 * @param {Lookup[]} [updates]
 * @returns {ArrayMap<Lookup>}
 */
function updateStorage(data: ArrayMap<Lookup>, updates?: Lookup[]): ArrayMap<Lookup> {
	if (logging) console.debug('⮂ lookupReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Lookup[]} [updates]
 * @returns {ArrayMap<Lookup>}
 */
function updateStorageNoReturn(updates?: Lookup[]): void {
	if (logging) console.debug('⮂ lookupReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Lookup[]} [data]
 * @returns {ArrayMap<Lookup>}
 */
function createState(data?: Lookup[] | ArrayMap<Lookup>): ArrayMap<Lookup> {
	if (!data) return ArrayMapRegistry.clone<Lookup>('lookup', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Lookup>('lookup', data.map(e => e));
    else return ArrayMapRegistry.clone<Lookup>('lookup', data);
}

function lookups(state: ArrayMap<Lookup>, action: LookupAction): ArrayMap<Lookup> {
	if (!state) state = createState();

	let st: ArrayMap<Lookup>;

	switch (action.type) {
		// action.lookup - the new value of lookup with id action.id
		// action.lookupId - id of the target, used if new value is null
		case Actions.LOOKUP_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ lookupReducer: FETCH_ONE_SUCCESS', action);

			if (action.lookup === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.lookupId)) {
				st = state.clone();
				st.set(action.lookup);
				// state.clear();

				updateStorageNoReturn([action.lookup]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.lookups
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.lookups
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.lookups
			 !partial, !stateEmpty, fetchEmpty	-> D return lookups in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge lookups in state newer than those in action.lookups
		 */
		case Actions.LOOKUP_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.lookups.length === 0;
			const fetched = action.lookups.map(lookup => new Lookup({...lookup, persisted: true}));

			if (logging) console.debug(`⮂ lookupReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} lookups with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ lookupReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ lookupReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Lookup> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Lookup[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(lookup => {
					const current = st.get(lookup.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && lookup.status === 'D') {
							// Remove it from state
							toUpdate.push(lookup);
							st.delete(lookup);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (lookup.addedOn > current.addedOn) {
								toUpdate.push(lookup);
								st.set(lookup);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (lookup.status !== 'D') {
							toUpdate.push(lookup);
							st.set(lookup);
						}
						else if (logging) console.debug('⮂ lookupReducer: partialMerge: skipping deleted lookup not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ lookupReducer: empty: lookups', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ lookupReducer: fullMerge: lookups', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localLookup => {
                    // If this lookup has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localLookup.persisted) {
                        const fetchedLookup = st.get(localLookup.id);
                        if (!fetchedLookup || localLookup.addedOn > fetchedLookup.addedOn) st.set(localLookup);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.LOOKUP_FETCH_ONE_FAILURE:
			console.error('LOOKUP_FETCH_ONE_FAILURE', action.lookupId, action.err);

			// No update to storage required
			return state;

		case Actions.LOOKUP_FETCH_FAILURE:
			console.error('LOOKUP_FETCH_FAILURE', action.lookupId, action.err);

			// No update to storage required
			return state;

		case Actions.LOOKUP_RESTORE: {
		    let lookup = new Lookup({...action.lookup});
            st = state.clone();

            const existing = st.get(lookup.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (lookup.addedOn >= existing.addedOn) {
                    st.set(lookup);
                    return updateStorage(st, [lookup]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (lookup.status === 'D') return state;

                st.set(lookup);
                return updateStorage(st, [lookup]);
            }
        }
            
		case Actions.LOOKUP_SAVE: {
		    // console.debug('⮂ LOOKUP_SAVE reducer', action.lookup?.id);
		    
			// clone the lookup
			let lookup = new Lookup({...action.lookup});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new lookup in the state
			// If this is a create, add to the queue
			const _lookup = st.get(lookup.id);

			if (!_lookup) st.set(lookup);
			else {
				if (lookup.addedOn >= _lookup.addedOn) st.set(lookup);
			}

			return updateStorage(st, [lookup]);
		}

		case Actions.LOOKUP_SAVE_MANY: {
		    if (logging) console.debug('⮂ LOOKUP_SAVE_MANY reducer', action.lookups.map(lookup => lookup.id).join(','));
		    
			// clone the lookups
			let toProcess = action.lookups.map(lookup => new Lookup(lookup));

			st = state.clone();
			// state.clear();

			const updates: Lookup[] = [];

			toProcess.forEach(lookup => {
				// Add or update the new lookup in the state
				// If this is a create, add to the queue
				const existing = st.get(lookup.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (lookup.status !== 'D') {
						st.push(lookup);
						updates.push(lookup);
					}
					else if (logging) console.debug('⮂ LOOKUP_SAVE_MANY: skipping deleted lookup not in state');
				}
				else {
					if (lookup.addedOn >= existing.addedOn) {
						st.set(lookup);
						updates.push(lookup);
					}
					else if (logging) console.debug('⮂ Skipping, existing lookup had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.LOOKUP_REMOVE: {
			// clone the lookup
			const lookup = new Lookup({...action.lookup});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(lookup);

			return updateStorage(st, [lookup]);
		}

		case Actions.LOOKUP_RESTORE_FAILURE:
		case Actions.LOOKUP_SAVE_FAILURE: {
		    console.warn('LOOKUP_SAVE_FAILURE reducer', action.lookup?.id);
		    if (!action.lookup?.id) {
				console.warn('LOOKUP_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const lookup = ensure(Lookup, action.lookup);
			if (!lookup) {
				console.error('Could not find lookup after call to save');
				return state;
			}

			st = state.clone();

			const _lookupRef = state.get(lookup.id);

			if (_lookupRef && (_lookupRef.addedOn === lookup.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _lookup = new Lookup({..._lookupRef});
				_lookup.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_lookup.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('lookup', _lookup).then(r => {
						if (r.isErr()) console.error(`⮂ lookupReducer: failed to save lookup with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ lookupReducer: saved lookup with fatalError info');
					}).catch(err => console.error(`⮂ lookupReducer: failed to save lookup with fatalError info: ${err.message}`));

				st.set(_lookup);
			}

			return updateStorage(st);
		}

		case Actions.LOOKUP_SAVE_MANY_FAILURE: {
		    console.warn('LOOKUP_SAVE_MANY_FAILURE reducer', action.lookups.map(lookup => lookup.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.LOOKUP_RESTORE_SUCCESS:
		case Actions.LOOKUP_SAVE_SUCCESS: {
			if (!action.lookup?.id) {
				console.warn('LOOKUP_SAVE_SUCCESS reducer');
                return state;
			}

			const lookupRef = state.get(action.lookup?.id);
			if (!lookupRef) {
				console.warn('Could not find lookup after call to save');
                return state;
			}

			st = state.clone();

			if (action.lookup.addedOn >= lookupRef.addedOn) {
				st.set(action.lookup);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const lookup = new Lookup({...lookupRef, persisted: true});
				st.set(lookup);
			}

			return updateStorage(st, [action.lookup]);
		}

		case Actions.LOOKUP_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ LOOKUP_SAVE_MANY_SUCCESS reducer', action.lookups.map(lookup => lookup.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const lookups: Lookup[] = [];

			action.lookups.forEach(e => {
				const lookupRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!lookupRef) console.warn('Could not find lookup after call to save');
				else if (e.addedOn >= lookupRef.addedOn) {
				    st.set(e);
				    lookups.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const lookup = new Lookup({...lookupRef, persisted: true});
					st.set(lookup);
					lookups.push(lookup);
				}
			});

			return updateStorage(st, lookups);
		}

		case Actions.LOOKUP_REMOVE_FAILURE: {
		    console.warn('LOOKUP_REMOVE_FAILURE action', action.lookup?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.LOOKUP_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.LOOKUP_RESTORE_MANY: {
			if (logging) console.debug('⮂ LOOKUP_RESTORE_MANY reducer', action.lookups.map(lookup => lookup.id).join(','));

			const toProcess = action.lookups.map(lookup => new Lookup({...lookup}));
			st = state.clone();

			const updates: Lookup[] = [];

			toProcess.forEach(lookup => {
				const existing = st.get(lookup.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (lookup.addedOn >= existing.addedOn) {
						st.set(lookup);
						updates.push(lookup);
					}
					else if (logging) console.debug('⮂ LOOKUP_RESTORE_MANY: skipping older version of lookup');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (lookup.status !== 'D') {
						st.set(lookup);
						updates.push(lookup);
					}
					else if (logging) console.debug('⮂ LOOKUP_RESTORE_MANY: skipping deleted lookup not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.LOOKUP_REMOVE_MANY: {
			if (logging) console.debug('⮂ LOOKUP_REMOVE_MANY reducer', action.lookups.map(lookup => lookup.id).join(','));

			const toProcess = action.lookups.map(lookup => new Lookup({...lookup}));
			st = state.clone();

			toProcess.forEach(lookup => {
				st.delete(lookup);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default lookups;